/**
 * YUL ON TAP - Main Application
 * 
 * OS-Style Architecture: Single app with vertical workspace switching.
 * Phase 2: Multi-engagement support with guest flows
 * Phase 3: Analytics dashboard + i18n support
 * Phase 3.5: Survey configuration
 * Industry Presentation Layer: Multi-industry entry points
 * SSO Bridge: Restaurant/Automotive/Healthcare redirect to live system
 * UI/UX Refinement: Light/Dark mode with time-based prompt
 * Super Admin: Control Center dashboard
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import DarkModePrompt from './components/DarkModePrompt';
import HelpCenter from './components/HelpCenter';
import brandConfig from './config/brand';
import './App.css';

// Pages - Admin
import Login from './pages/Login';
import SelectVertical from './pages/SelectVertical';
import AccountSettings from './pages/AccountSettings';
import SSOReceiver from './pages/SSOReceiver';
import RestaurantDashboard from './pages/RestaurantDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import HotelsDashboard from './pages/hotels/HotelsDashboard';
import HotelForm from './pages/hotels/HotelForm';
import UnifiedPropertyPage from './pages/hotels/UnifiedPropertyPage';
import HotelGroupsList from './pages/hotels/HotelGroupsList';
import HotelGroupManagement from './pages/hotels/HotelGroupManagement';
import SurveyConfiguration from './pages/hotels/SurveyConfiguration';
import RestroomSurveyConfiguration from './pages/hotels/RestroomSurveyConfiguration';
import RetailerAlertConfiguration from './pages/hotels/RetailerAlertConfiguration';
import HotelsLogin from './pages/hotels/HotelsLogin';
import ExecutiveDashboard from './pages/hotels/ExecutiveDashboard';
import PlatformValuesPage from './pages/hotels/PlatformValuesPage';
import PlatformReviewValues from './pages/hotels/PlatformReviewValues';
import PropertyComparisonDashboard from './pages/hotels/PropertyComparisonDashboard';
import IncidentData from './pages/hotels/IncidentData';
import URLRegistry from './pages/hotels/URLRegistry';
import CommunicationsMessages from './pages/hotels/CommunicationsMessages';

// Pages - Guest (Public)
import GuestReview from './pages/guest/GuestReview';
import GuestSurvey from './pages/guest/GuestSurvey';
import TapHandler from './pages/guest/TapHandler';

// Standalone Hotels Portal (Isolated Vertical)
import { StandaloneDashboard, StandaloneLogin, StandaloneTapHandler, StandaloneSurvey, StandaloneCards, RestroomTapHandler, RestroomSurvey, RetailerAlertTapHandler, RetailerAlertSurvey, GuidedCardSetup } from './pages/standalone';
import CommunicationsView from './pages/standalone/CommunicationsView';

// NEW: Explicit separated handlers for Review Cards and Room Tags
import ReviewTapHandler from './pages/standalone/ReviewTapHandler';
import RoomTapHandler from './pages/standalone/RoomTapHandler';
import AlertAcknowledged from './pages/hotels/AlertAcknowledged';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for Hotels - redirects to /hotels/login
const HotelsProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/hotels/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to app if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, getPostLoginRoute } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Route based on user's actual workspace permissions
    const route = getPostLoginRoute();
    return <Navigate to={route} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      {/* HOTELS-ONLY PORTAL: /login redirects to /hotels/login */}
      <Route
        path="/login"
        element={<Navigate to="/hotels/login" replace />}
      />
      
      {/* Guest Engagement Routes (Public - no auth) */}
      <Route path="/tap/:tagId" element={<TapHandler />} />
      <Route path="/review/:hotelId/:roomId" element={<GuestReview />} />
      <Route path="/survey/:hotelId/:roomId" element={<GuestSurvey />} />
      
      {/* SSO Receiver - Validates token and creates session */}
      <Route path="/sso" element={<SSOReceiver />} />
      
      {/* Restaurant Dashboard - Destination for SSO redirects */}
      <Route
        path="/restaurant-dashboard"
        element={
          <ProtectedRoute>
            <RestaurantDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/select-vertical"
        element={
          <ProtectedRoute>
            <SelectVertical />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect /dashboard to /select-vertical (core workspace entry) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Navigate to="/select-vertical" replace />
          </ProtectedRoute>
        }
      />
      
      {/* Account Settings */}
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        }
      />
      
      {/* Super Admin Dashboard */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Hotel Vertical Routes */}
      <Route path="/hotels/login" element={<HotelsLogin />} />
      <Route
        path="/hotels"
        element={
          <HotelsProtectedRoute>
            <HotelsDashboard />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/executive"
        element={
          <HotelsProtectedRoute>
            <ExecutiveDashboard />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/platform-values"
        element={
          <HotelsProtectedRoute>
            <PlatformValuesPage />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/review-values"
        element={
          <HotelsProtectedRoute>
            <PlatformReviewValues />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/compare"
        element={
          <HotelsProtectedRoute>
            <PropertyComparisonDashboard />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/incidents"
        element={
          <HotelsProtectedRoute>
            <IncidentData />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/url-registry"
        element={
          <HotelsProtectedRoute>
            <URLRegistry />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/groups"
        element={
          <HotelsProtectedRoute>
            <HotelGroupsList />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/groups/:groupId"
        element={
          <HotelsProtectedRoute>
            <HotelGroupManagement />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/new"
        element={
          <HotelsProtectedRoute>
            <HotelForm />
          </HotelsProtectedRoute>
        }
      />
      {/* Unified Property Page - Single page for all property management */}
      <Route
        path="/hotels/:id"
        element={
          <HotelsProtectedRoute>
            <UnifiedPropertyPage />
          </HotelsProtectedRoute>
        }
      />
      {/* Legacy routes redirect to unified page */}
      <Route
        path="/hotels/:id/manage"
        element={
          <HotelsProtectedRoute>
            <UnifiedPropertyPage />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/edit"
        element={
          <HotelsProtectedRoute>
            <HotelForm />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/rooms"
        element={
          <HotelsProtectedRoute>
            <UnifiedPropertyPage />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/engagement"
        element={
          <HotelsProtectedRoute>
            <UnifiedPropertyPage />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/analytics"
        element={
          <HotelsProtectedRoute>
            <UnifiedPropertyPage />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/survey-config"
        element={
          <HotelsProtectedRoute>
            <SurveyConfiguration />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/restroom-survey-config"
        element={
          <HotelsProtectedRoute>
            <RestroomSurveyConfiguration />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:id/retailer-alert-config"
        element={
          <HotelsProtectedRoute>
            <RetailerAlertConfiguration />
          </HotelsProtectedRoute>
        }
      />
      <Route
        path="/hotels/:orgId/communications"
        element={
          <HotelsProtectedRoute>
            <CommunicationsMessages />
          </HotelsProtectedRoute>
        }
      />
      
      {/* Restaurant Vertical - Redirect to Restaurant Dashboard */}
      <Route
        path="/restaurants"
        element={
          <ProtectedRoute>
            <RestaurantDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect - HOTELS-ONLY PORTAL */}
      <Route path="/" element={<Navigate to="/hotels/login" replace />} />
      
      {/* Alert Acknowledgment Page (Public - No Auth) */}
      <Route path="/alert-acknowledged" element={<AlertAcknowledged />} />
      
      {/* ============================================ */}
      {/* STANDALONE HOTELS PORTAL (Isolated Vertical) */}
      {/* ============================================ */}
      
      {/* Standalone Login - Direct to Hotels (No Industry Selector) */}
      <Route path="/standalone/login" element={<StandaloneLogin />} />
      
      {/* Standalone Dashboard */}
      <Route path="/standalone/dashboard" element={<StandaloneDashboard />} />
      <Route path="/standalone" element={<Navigate to="/standalone/dashboard" replace />} />
      
      {/* Standalone NFC Tap Handler (Public - No Auth) */}
      {/* DEPRECATED: This ambiguous route will be removed in future */}
      <Route path="/standalone/tap/:cardId" element={<StandaloneTapHandler />} />
      
      {/* ============================================ */}
      {/* NEW: EXPLICIT SEPARATED ROUTES (No Ambiguity) */}
      {/* ============================================ */}
      
      {/* REVIEW ENGINE CARDS - Always goes to review platform */}
      {/* URL: /standalone/review/{CARD_ID} */}
      <Route path="/standalone/review/:cardId" element={<ReviewTapHandler />} />
      
      {/* ROOM ARRIVAL TAGS - Always goes to check-in survey */}
      {/* URL: /standalone/room/{TAG_ID} */}
      <Route path="/standalone/room/:tagId" element={<RoomTapHandler />} />
      
      {/* RESTROOM SURVEY TAGS - Always goes to restroom feedback survey */}
      {/* URL: /standalone/restroom/{TAG_ID} */}
      <Route path="/standalone/restroom/:tagId" element={<RestroomTapHandler />} />
      
      {/* Restroom Survey Page (Public - No Auth) */}
      <Route path="/standalone/restroom-survey/:orgId/:locationId" element={<RestroomSurvey />} />
      <Route path="/restroom-survey/:orgId/:locationId" element={<RestroomSurvey />} />

      {/* RETAILER ALERT TAGS - Category-based reporting for retail stores */}
      {/* URL: /standalone/retailer-alert/{TAG_ID} */}
      <Route path="/standalone/retailer-alert/:tagId" element={<RetailerAlertTapHandler />} />
      
      <Route path="/standalone/retailer-alert-survey/:orgId/:locationId" element={<RetailerAlertSurvey />} />
      <Route path="/retailer-alert-survey/:orgId/:locationId" element={<RetailerAlertSurvey />} />

      {/* Pyramid 3: Communications View (Public - No Auth) */}
      <Route path="/standalone/communications-view/:orgId" element={<CommunicationsView />} />
      
      {/* Standalone Survey (Public - No Auth) */}
      <Route path="/standalone/survey/:orgId/:locationId" element={<StandaloneSurvey />} />
      
      {/* Standalone Cards Management */}
      <Route path="/standalone/cards" element={<StandaloneCards />} />
      
      {/* Guided Card Setup Wizard */}
      <Route path="/standalone/setup-card" element={<GuidedCardSetup />} />
      
      {/* Standalone Organization/Property Page */}
      <Route path="/standalone/organization/:id" element={<UnifiedPropertyPage />} />
      
      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = brandConfig.portalTitle;
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <DarkModePrompt />
          <AuthenticatedHelpCenter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Only show HelpCenter when user is authenticated
function AuthenticatedHelpCenter() {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) return null;
  
  return <HelpCenter />;
}

export default App;
